import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSeoTags } from "@/lib/seo";

/**
 * VOIHU — /why-voihu.
 *
 * Explains: what VOIHU is, what a person does, what happens after they join,
 * why the small contribution exists, and what happens when a message grows.
 *
 * Motion: pure CSS transitions + IntersectionObserver-triggered reveals.
 * No animation libraries. Respects `prefers-reduced-motion`. Content is
 * always readable even if all animation is disabled.
 */
export default function WhyVoihuPage() {
  useSeoTags({
    title: "Why VOIHU — One message. Many people. Real weight.",
    description:
      "VOIHU is a platform where people come together around messages " +
      "they believe in and help carry them from person to person.",
    canonical: "https://voihuworld.com/why-voihu",
    ogType: "website",
  });

  return (
    <main
      className="bg-[color:var(--oh-bg)] text-[color:var(--oh-ink)]"
      data-testid="why-voihu-page"
    >
      {/* ── Opening ─────────────────────────────────────────────────── */}
      <section className="max-w-[900px] mx-auto px-6 sm:px-10 pt-16 sm:pt-24 pb-16 sm:pb-20">
        <Reveal>
          <div className="oh-label" data-testid="why-voihu-eyebrow">
            Why VOIHU
          </div>
        </Reveal>
        <Reveal delay={80}>
          <h1
            className="font-serif-editorial text-[3rem] sm:text-[4.5rem] lg:text-[6rem]
                       leading-[0.98] tracking-tight mt-6"
            data-testid="why-voihu-h1"
          >
            One message.<br />
            Many people.<br />
            <span className="text-[color:var(--oh-red)]">Real weight.</span>
          </h1>
        </Reveal>
        <Reveal delay={160}>
          <p className="mt-10 text-lg sm:text-xl leading-[1.65] text-[color:var(--oh-ink-2)] max-w-[720px]">
            VOIHU is a platform where people come together around messages
            they believe in and help carry them from person to person.
            Instead of measuring only who saw something, VOIHU makes it
            possible to see how many people chose to stand behind the same
            message. As that number grows, a message can gain real weight.
          </p>
        </Reveal>
      </section>

      {/* ── How it works ────────────────────────────────────────────── */}
      <section className="border-t oh-hairline">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 py-16 sm:py-20">
          <Reveal>
            <div className="oh-label">How it works</div>
          </Reveal>
          <Reveal delay={60}>
            <h2 className="font-serif-editorial text-2xl sm:text-4xl leading-[1.1] mt-3 tracking-tight">
              Five steps. One human chain.
            </h2>
          </Reveal>

          <ol className="mt-10 relative" data-testid="how-it-works">
            {/* Vertical timeline line */}
            <span
              aria-hidden="true"
              className="hidden sm:block absolute left-[20px] top-0 bottom-0 w-px bg-[color:var(--oh-ink-3)]/40"
            />
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={60 + i * 30}>
                <li
                  className="relative flex gap-5 sm:gap-8 pb-8 last:pb-0"
                  data-testid={`how-step-${s.n}`}
                >
                  <div className="shrink-0 relative z-10">
                    <div
                      className="w-10 h-10 rounded-full border oh-hairline bg-[color:var(--oh-bg)]
                                 flex items-center justify-center text-sm font-medium
                                 text-[color:var(--oh-ink)]"
                      style={{
                        fontFamily: "Manrope, ui-sans-serif, system-ui, -apple-system, sans-serif",
                        fontVariantNumeric: "lining-nums tabular-nums",
                        fontFeatureSettings: "'lnum' 1, 'tnum' 1",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {s.n}
                    </div>
                  </div>
                  <div className="pt-1.5">
                    <div className="oh-label text-[color:var(--oh-red)]">
                      {s.label}
                    </div>
                    <h3 className="font-serif-editorial text-xl sm:text-2xl leading-[1.2] mt-1.5 tracking-tight max-w-[640px]">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm sm:text-base leading-[1.6] text-[color:var(--oh-ink-2)] max-w-[620px]">
                      {s.body}
                    </p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ── It doesn't end when you join ────────────────────────────── */}
      <section className="border-t oh-hairline bg-[color:var(--oh-ink)] text-[color:var(--oh-bg)]">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 py-16 sm:py-24 text-center">
          <Reveal>
            <div className="oh-label text-[color:var(--oh-bg)]/70">
              What happens next
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif-editorial text-[2rem] sm:text-[3rem] lg:text-[3.75rem]
                           leading-[1.05] tracking-tight mt-4">
              It doesn’t end<br />when you join.
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 mx-auto max-w-[600px] text-base sm:text-lg leading-[1.6] text-[color:var(--oh-bg)]/75">
              You can follow what happens to the message, see how it grows and
              receive updates when it reaches important milestones or takes a
              meaningful step forward.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── One meaning. One message. Many voices. ──────────────────── */}
      <section className="border-t oh-hairline">
        <div className="max-w-[1000px] mx-auto px-6 sm:px-10 py-24 sm:py-36">
          <Reveal>
            <div className="oh-label">The one-meaning principle</div>
          </Reveal>
          <Reveal delay={80}>
            <h2 className="mt-6 font-serif-editorial text-[2.5rem] sm:text-[4rem] lg:text-[5.5rem]
                           leading-[1.02] tracking-tight">
              One meaning.<br />
              One message.<br />
              <span className="text-[color:var(--oh-red)]">Many voices.</span>
            </h2>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-10 text-lg sm:text-xl leading-[1.65] text-[color:var(--oh-ink-2)] max-w-[640px]">
              Thousands of versions of the same idea can divide its strength.
              VOIHU is designed to bring people who mean the same thing around
              one shared message. More people. One stronger collective voice.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <section className="border-t oh-hairline">
        <div className="max-w-[820px] mx-auto px-6 sm:px-10 py-16 sm:py-24">
          <Reveal>
            <div className="oh-label">FAQ</div>
          </Reveal>
          <Reveal delay={60}>
            <h2 className="font-serif-editorial text-2xl sm:text-4xl leading-[1.1] mt-3 tracking-tight">
              Questions you may be asking.
            </h2>
          </Reveal>

          <Faq items={FAQS} />
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────── */}
      <section className="border-t oh-hairline bg-[color:var(--oh-red)] text-white">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10 py-24 sm:py-32 text-center">
          <Reveal>
            <h2 className="font-serif-editorial text-[2.5rem] sm:text-[4rem] lg:text-[5rem]
                           leading-[1.02] tracking-tight">
              Is there something<br />you believe in?
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="mt-10 text-xl sm:text-2xl leading-[1.35]">
              Find the message.<br />
              Add your Voice.<br />
              Pass it on.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <Link
              to="/explore"
              className="inline-block mt-12 bg-white text-[color:var(--oh-ink)]
                         px-8 py-4 oh-label hover:bg-[color:var(--oh-bg)] transition-colors"
              data-testid="why-voihu-explore-cta"
            >
              Explore messages
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

// ── Data ──────────────────────────────────────────────────────────────
const STEPS = [
  {
    n: "01",
    label: "Choose",
    title: "Find a message that represents something you believe in.",
    body:
      "If that meaning already exists, you don’t need to create another " +
      "version. You can join the people already carrying it forward.",
  },
  {
    n: "02",
    label: "Join",
    title: "Add your Voice.",
    body:
      "It isn’t a like or a view. It’s your choice to stand behind that " +
      "message.",
  },
  {
    n: "03",
    label: "Pass it on",
    title: "Share it with other people.",
    body:
      "If they choose to join and pass it forward too, the chain continues.",
  },
  {
    n: "04",
    label: "Help it grow",
    title:
      "VOIHU measures how many people choose to stand behind the message.",
    body:
      "You can see how that collective voice grows over time.",
  },
  {
    n: "05",
    label: "Turn numbers into weight",
    title:
      "When a message reaches meaningful participation, those numbers shouldn’t stay inside VOIHU.",
    body:
      "The goal is to turn that collective participation into leverage — " +
      "bringing the message and the strength behind it to the attention of " +
      "organizations, companies, institutions or others directly involved " +
      "who can listen, respond or act.",
  },
];

const FAQS = [
  {
    q: "Why is there a small contribution to join?",
    a: (
      <>
        <p>
          Because we want VOIHU to remain independent. We don’t want
          advertising, sponsors or outside interests deciding which messages
          deserve to travel.
        </p>
        <p className="mt-4">
          The small contribution required to add your Voice helps support
          VOIHU and, at the same time, creates a meaningful barrier against
          automated, fake or mass participation.
        </p>
        <p className="mt-4">
          It isn’t the price of a message. It’s a way to make every Voice
          more real, intentional and independent.
        </p>
      </>
    ),
  },
  {
    q: "How do I know if the message is growing?",
    a: (
      <p>
        VOIHU measures the participation behind a message and the path of its
        propagation. You can see how many people have joined, where the
        message has reached and, through MY IMPACT, how your participation
        and the people who came through you contributed to its growth.
      </p>
    ),
  },
  {
    q: "What happens when enough people join?",
    a: (
      <>
        <p>This is where the numbers should start to matter.</p>
        <p className="mt-4">
          When a message reaches meaningful participation, VOIHU’s goal is to
          make sure that collective strength doesn’t remain only on the
          platform. The results can become leverage and, when appropriate,
          VOIHU can bring the message to the attention of organizations,
          companies, institutions or others directly involved.
        </p>
        <p className="mt-4">
          And the people who joined can know what happens next.
        </p>
      </>
    ),
  },
  {
    q: "Why isn’t a like enough?",
    a: (
      <p>
        Because seeing something doesn’t necessarily mean standing behind it.
        VOIHU doesn’t only want to know how many people saw a message. It
        makes visible how many people chose to join it and help carry it
        forward.
      </p>
    ),
  },
  {
    q: "Can I create a message that already exists?",
    a: (
      <>
        <p>VOIHU follows a simple principle:</p>
        <p className="mt-2 font-serif-editorial text-xl">
          One meaning. One message. Many voices.
        </p>
        <p className="mt-4">
          If a message with the same meaning already exists, VOIHU is
          designed to bring participation together instead of dividing it
          across copies of the same idea.
        </p>
      </>
    ),
  },
  {
    q: "Who decides which messages to support?",
    a: (
      <>
        <p>People do.</p>
        <p className="mt-4">
          VOIHU isn’t built to tell you what to think. It lets people choose
          which messages they want to stand behind and makes the
          participation those messages gather visible.
        </p>
      </>
    ),
  },
];

// ── Helpers ───────────────────────────────────────────────────────────

/**
 * IntersectionObserver-driven reveal. Content is present in the DOM from
 * the first render (SEO / crawler / no-JS friendly); only the CSS-driven
 * opacity + translate is delayed until the element enters the viewport.
 * `prefers-reduced-motion` short-circuits to fully visible on mount.
 */
function Reveal({ children, delay = 0 }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") { setShown(true); return; }
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) { setShown(true); return; }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: "-10% 0px -10% 0px", threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
      className={
        "transition-all duration-700 ease-out will-change-[opacity,transform] " +
        (shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")
      }
    >
      {children}
    </div>
  );
}

/**
 * Accessible single-open accordion. Uses real `<button>` triggers with
 * `aria-expanded` + `aria-controls`, so screen readers and keyboard users
 * navigate naturally. Only one panel is open at a time; keys are the
 * standard native button semantics (Space / Enter to toggle).
 */
function Faq({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="mt-10 border-t oh-hairline" data-testid="faq">
      {items.map((it, i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="border-b oh-hairline">
            <h3>
              <button
                type="button"
                id={`faq-btn-${i}`}
                aria-expanded={isOpen}
                aria-controls={`faq-panel-${i}`}
                onClick={() => setOpen(isOpen ? null : i)}
                className="w-full flex items-center justify-between gap-6 text-left
                           py-6 sm:py-8 group"
                data-testid={`faq-question-${i}`}
              >
                <span className="font-serif-editorial text-xl sm:text-2xl leading-[1.25] tracking-tight
                                 text-[color:var(--oh-ink)] group-hover:text-[color:var(--oh-red)] transition-colors">
                  {it.q}
                </span>
                <span
                  aria-hidden="true"
                  className={
                    "shrink-0 w-6 h-6 border oh-hairline flex items-center justify-center " +
                    "text-[color:var(--oh-ink)] transition-transform duration-300 " +
                    (isOpen ? "rotate-45" : "rotate-0")
                  }
                >
                  +
                </span>
              </button>
            </h3>
            <div
              id={`faq-panel-${i}`}
              role="region"
              aria-labelledby={`faq-btn-${i}`}
              hidden={!isOpen}
              className="pb-8 pr-10 text-base sm:text-lg leading-[1.7] text-[color:var(--oh-ink-2)]"
              data-testid={`faq-answer-${i}`}
            >
              {it.a}
            </div>
          </div>
        );
      })}
    </div>
  );
}
